import { differenceInCalendarDays, format, isValid } from 'date-fns';
import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';

import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';

import useRedeemPartnerKey from '../../../hooks/dashboard/settings/useRedeemPartnerKey';
import { RedeemedPartnerKeyInterface } from '../../../models/dashboard/settings/third-party-access.model';

const maskKey = (key: string) =>
  key.length <= 10 ? '•'.repeat(key.length) : `${key.slice(0, 6)}${'•'.repeat(18)}${key.slice(-4)}`;

const shortToken = (token: string) =>
  token.length > 16 ? `${token.slice(0, 12)}…` : token;

const ThirdPartyApiGeneration = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const { redeemPartnerKey, redeemedKey, errorMessage, setLoading, loading } =
    useRedeemPartnerKey();
  const hasRequested = useRef(false);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    // The token is single-use, so never send it twice
    if (hasRequested.current) return;
    hasRequested.current = true;
    redeemPartnerKey(token);
  }, [token, redeemPartnerKey, setLoading]);

  const renderTerminalBody = () => {
    if (loading) {
      return (
        <>
          <PromptLine token={token} />
          <Muted>
            Redeeming token<Cursor />
          </Muted>
        </>
      );
    }

    if (!token || !redeemedKey) {
      return (
        <>
          {token && <PromptLine token={token} />}
          <ErrorLine>
            ✖ {token ? errorMessage : 'No token found in this link.'}
          </ErrorLine>
          <Muted>
            # Ask the company that invited you to send a new link.
          </Muted>
        </>
      );
    }

    return (
      <>
        <PromptLine token={token} />
        <SuccessLine>✔ 200 OK · key generated</SuccessLine>
        <ResponseJson data={redeemedKey} />
      </>
    );
  };

  return (
    <Container>
      <Content>
        <Brand>
          <BrandDot />
          logirate <span>/ partner api</span>
        </Brand>

        {redeemedKey ? (
          <Heading>
            <h1>Your API key is ready</h1>
            <p>
              <strong>{redeemedKey.companyName}</strong> has given you access
              to the Logirate API. Copy your key below and keep it secret.
            </p>
          </Heading>
        ) : (
          <Heading>
            <h1>{loading ? 'Generating your key…' : "We couldn't generate your key"}</h1>
            <p>
              {loading
                ? 'Hang tight, this only takes a second.'
                : 'This link may have expired or already been used.'}
            </p>
          </Heading>
        )}

        <Terminal>
          <TerminalBar>
            <span className='dot red' />
            <span className='dot yellow' />
            <span className='dot green' />
            <TerminalTitle>~/logirate — key.json</TerminalTitle>
          </TerminalBar>
          <TerminalBody aria-live='polite'>{renderTerminalBody()}</TerminalBody>
        </Terminal>

        {redeemedKey && <KeyPanel data={redeemedKey} />}
      </Content>
    </Container>
  );
};

export default ThirdPartyApiGeneration;

const PromptLine: React.FC<{ token: string }> = ({ token }) => (
  <Line>
    <Prompt>$</Prompt> logirate keys redeem <Flag>--token</Flag>{' '}
    <StringToken>{shortToken(token)}</StringToken>
  </Line>
);

const ResponseJson: React.FC<{ data: RedeemedPartnerKeyInterface }> = ({
  data,
}) => {
  const entries: [string, string][] = [
    ['plainKey', maskKey(data.plainKey)],
    ['companyName', data.companyName],
    ['expiresAt', data.expiresAt],
  ];

  return (
    <Json>
      <Punctuation>{'{'}</Punctuation>
      {entries.map(([key, value], index) => (
        <JsonRow key={key}>
          <JsonKey>"{key}"</JsonKey>
          <Punctuation>: </Punctuation>
          <StringToken>"{value}"</StringToken>
          {index < entries.length - 1 && <Punctuation>,</Punctuation>}
        </JsonRow>
      ))}
      <Punctuation>{'}'}</Punctuation>
    </Json>
  );
};

const KeyPanel: React.FC<{ data: RedeemedPartnerKeyInterface }> = ({ data }) => {
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);

  const expiry = new Date(data.expiresAt);
  const hasExpiry = isValid(expiry);
  const daysLeft = hasExpiry ? differenceInCalendarDays(expiry, new Date()) : 0;

  const handleCopy = () => {
    navigator.clipboard.writeText(data.plainKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Panel>
      <PanelLabel>API key</PanelLabel>
      <KeyRow>
        <KeyValue>{revealed ? data.plainKey : maskKey(data.plainKey)}</KeyValue>
        <KeyActions>
          <GhostButton type='button' onClick={() => setRevealed(!revealed)}>
            {revealed ? 'Hide' : 'Show'}
          </GhostButton>
          <CopyButton type='button' onClick={handleCopy} copied={copied}>
            {copied ? 'Copied ✓' : 'Copy'}
          </CopyButton>
        </KeyActions>
      </KeyRow>

      <MetaRow>
        {hasExpiry && (
          <MetaItem>
            <MetaLabel>Expires</MetaLabel>
            {format(expiry, 'MMM dd, yyyy')}
            <ExpiryPill soon={daysLeft <= 7}>
              {daysLeft <= 0
                ? 'today'
                : `in ${daysLeft} day${daysLeft === 1 ? '' : 's'}`}
            </ExpiryPill>
          </MetaItem>
        )}
        <MetaItem>
          <MetaLabel>Issued by</MetaLabel>
          {data.companyName}
        </MetaItem>
      </MetaRow>

      <Tip>
        🔒 Store this key somewhere safe, like a secrets manager or{' '}
        <code>.env</code> file, before leaving this page. Never commit it to
        git.
      </Tip>
    </Panel>
  );
};

// Styles

const mono = `'JetBrains Mono', 'Fira Code', 'SFMono-Regular', Menlo, Consolas, monospace`;

const blink = keyframes`
  50% { opacity: 0; }
`;

const rise = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Container = styled.div`
  min-height: 100vh;
  background-color: ${({ theme }) => theme.palette.pageBgColor};
  background-image:
    radial-gradient(
      circle at 20% 0%,
      ${({ theme }) => theme.palette.logiratePrimaryLight},
      transparent 45%
    ),
    linear-gradient(${({ theme }) => theme.palette.borderColor} 1px, transparent 1px),
    linear-gradient(90deg, ${({ theme }) => theme.palette.borderColor} 1px, transparent 1px);
  background-size: auto, 32px 32px, 32px 32px;
  color: ${({ theme }) => theme.palette.primaryTextColor};
  display: flex;
  justify-content: center;
  padding: 64px 16px;

  @media (max-width: 884px) {
    padding: 40px 16px;
  }
`;

const Content = styled.div`
  width: 100%;
  max-width: 640px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  animation: ${rise} 0.4s ease-out;
`;

const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: ${mono};
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.logirateBlack};

  span {
    color: ${({ theme }) => theme.palette.grayTextColor};
    font-weight: 400;
  }
`;

const BrandDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.palette.blueColor};
  box-shadow: 0 0 0 4px ${({ theme }) => theme.palette.logiratePrimaryLight};
`;

const Heading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  h1 {
    margin: 0;
    font-size: 32px;
    line-height: 40px;
    font-weight: 700;
    color: ${({ theme }) => theme.palette.logirateBlack};

    @media (max-width: 884px) {
      font-size: 24px;
      line-height: 32px;
    }
  }

  p {
    margin: 0;
    font-size: 15px;
    line-height: 24px;
    color: ${({ theme }) => theme.palette.grayTextColor};
  }

  strong {
    color: ${({ theme }) => theme.palette.primaryTextColor};
  }
`;

const Terminal = styled.div`
  border: 1px solid ${({ theme }) => theme.palette.borderColor};
  border-radius: 12px;
  background-color: ${({ theme }) => theme.palette.bgWhiteColor};
  overflow: hidden;
  box-shadow: 0 12px 32px ${({ theme }) => theme.palette.boxShadowColor};
`;

const TerminalBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background-color: ${({ theme }) => theme.palette.tertiaryBgColor};
  border-bottom: 1px solid ${({ theme }) => theme.palette.borderColor};

  .dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;
  }
  .red {
    background-color: ${({ theme }) => theme.palette.warningTextColor};
  }
  .yellow {
    background-color: ${({ theme }) => theme.palette.darkBorderColor};
  }
  .green {
    background-color: ${({ theme }) => theme.palette.blueColor};
  }
`;

const TerminalTitle = styled.div`
  flex: 1;
  text-align: center;
  margin-right: 52px;
  font-family: ${mono};
  font-size: 12px;
  color: ${({ theme }) => theme.palette.formFooterText};
`;

const TerminalBody = styled.div`
  padding: 20px;
  font-family: ${mono};
  font-size: 13px;
  line-height: 22px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  overflow-x: auto;
  background-color: ${({ theme }) => theme.palette.blueGrayBg};
  color: ${({ theme }) => theme.palette.secondaryTextColor};
`;

const Line = styled.div`
  white-space: nowrap;
`;

const Prompt = styled.span`
  color: ${({ theme }) => theme.palette.greenColor};
  font-weight: 700;
`;

const Flag = styled.span`
  color: ${({ theme }) => theme.palette.mediumGrayText};
`;

const StringToken = styled.span`
  color: ${({ theme }) => theme.palette.greenColor};
  word-break: break-all;
`;

const Muted = styled.div`
  color: ${({ theme }) => theme.palette.formFooterText};
`;

const Cursor = styled.span`
  display: inline-block;
  width: 8px;
  height: 15px;
  margin-left: 4px;
  vertical-align: text-bottom;
  background-color: ${({ theme }) => theme.palette.blueColor};
  animation: ${blink} 1s step-end infinite;
`;

const SuccessLine = styled.div`
  color: ${({ theme }) => theme.palette.greenColor};
  font-weight: 600;
`;

const ErrorLine = styled.div`
  color: ${({ theme }) => theme.palette.redFontColor};
  font-weight: 600;
`;

const Json = styled.div`
  margin-top: 4px;
`;

const JsonRow = styled.div`
  padding-left: 20px;
`;

const JsonKey = styled.span`
  color: ${({ theme }) => theme.palette.blueColor};
`;

const Punctuation = styled.span`
  color: ${({ theme }) => theme.palette.darkestBorderColor};
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  border: 1px solid ${({ theme }) => theme.palette.borderColor};
  border-radius: 12px;
  background-color: ${({ theme }) => theme.palette.bgWhiteColor};
  box-shadow: 0 12px 32px ${({ theme }) => theme.palette.boxShadowColor};
`;

const PanelLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.palette.grayTextColor};
`;

const KeyRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 12px 12px 16px;
  border: 1px dashed ${({ theme }) => theme.palette.logiratePrimary};
  border-radius: 8px;
  background-color: ${({ theme }) => theme.palette.logiFormBackgroundHighlight};

  @media (max-width: 884px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const KeyValue = styled.code`
  flex: 1;
  min-width: 0;
  font-family: ${mono};
  font-size: 14px;
  color: ${({ theme }) => theme.palette.primaryTextColor};
  word-break: break-all;
`;

const KeyActions = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
`;

const GhostButton = styled.button`
  padding: 8px 14px;
  border: 1px solid ${({ theme }) => theme.palette.darkBorderColor};
  border-radius: 6px;
  background-color: ${({ theme }) => theme.palette.bgWhiteColor};
  color: ${({ theme }) => theme.palette.secondaryTextColor};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.palette.veryLightGrayBg};
  }
`;

const CopyButton = styled.button<{ copied: boolean }>`
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  background-color: ${({ copied, theme }) =>
    copied ? theme.palette.greenColor : theme.palette.blueColor};
  color: ${({ theme }) => theme.palette.bgWhiteColor};
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  min-width: 92px;
  transition: background-color 0.2s ease;
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: ${({ theme }) => theme.palette.primaryTextColor};
`;

const MetaLabel = styled.span`
  color: ${({ theme }) => theme.palette.grayTextColor};
`;

const ExpiryPill = styled.span<{ soon: boolean }>`
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ soon, theme }) =>
    soon ? theme.palette.warningTextColor : theme.palette.blueColor};
  background-color: ${({ soon, theme }) =>
    soon ? theme.palette.warningBgColor : theme.palette.logiratePrimaryLight};
`;

const Tip = styled.div`
  font-size: 13px;
  line-height: 20px;
  color: ${({ theme }) => theme.palette.grayTextColor};
  padding-top: 16px;
  border-top: 1px solid ${({ theme }) => theme.palette.borderColor};

  code {
    font-family: ${mono};
    color: ${({ theme }) => theme.palette.primaryTextColor};
  }
`;
